import React, { useState } from 'react'
import { Col, Container, Form, Row } from 'react-bootstrap'
import { Modal } from '../../../../components/Modal/Modal'
import { Button } from '../../../../components/Button/Button'
import { SelectDropdown } from '../../../../components/SelectDropdown/SelectDropdown'
import { LANGUAGE_OPTIONS } from '../../../../common/languageUtils'
import { MeetingLanguage } from '../../../../models/Room'
import { useMultiLanguageEnabled } from '../../../../common/useMultiLanguageEnabled'
import { useUpdateAccountMutation } from '../../../../store/accountUserSettings/accountUserSettings'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useAccount } from '../../../../common/useAccount'

type LanguageSettingModalProps = {
  onHide: () => void
  onEnableClicked: (selectedLanguage: MeetingLanguage) => void
}

export const LanguageSettingModal: React.FC<LanguageSettingModalProps> = ({ onHide, onEnableClicked }) => {
  const modalHeader = <h4 className='mb-3'>Enable multi language support</h4>
  const { defaultLanguage } = useMultiLanguageEnabled()
  const [selectedLanguage, setSelectedLanguage] = useState<MeetingLanguage>(defaultLanguage)
  const accountId = useAppSelector(selectAccountId)
  const { account, isFetching: isAccountFetching } = useAccount({ refetchOnMountOrArgChange: true })
  const [updateAccount, { isLoading: isAccountLoading }] = useUpdateAccountMutation()

  const isAutoDetect = selectedLanguage === MeetingLanguage.AUTO_DETECT
  const selectedLanguageOption = LANGUAGE_OPTIONS.find((lang) => lang.id === selectedLanguage) || LANGUAGE_OPTIONS[0]

  const enableMultiLanguage = () => {
    if (!account) {
      return
    }
    const accountConfig = {
      ...account.config,
      multiLanguageSupport: { enabled: true, defaultLanguage: selectedLanguage },
    }
    updateAccount({
      accountId: accountId,
      payload: {
        config: accountConfig,
      },
    }).then(() => {
      onEnableClicked(selectedLanguage)
    })
  }
  const body = (
    <Container>
      <Row>
        <Col>
          <div className='pb-4'>
            Zocks determines meeting language <strong>based on your clients' language preferences</strong>. Choose the fallback option when client preferences
            don't indicate a common language.
          </div>
          <div className='d-flex flex-column gap-2'>
            <div className='d-flex flex-column'>
              <Form.Check
                type='radio'
                id='default-language-option'
                name='language-option'
                label='Fallback language'
                checked={!isAutoDetect}
                onChange={() => setSelectedLanguage(LANGUAGE_OPTIONS[0].id as MeetingLanguage)}
                className='paragraph-medium'
              />
            </div>
            <div className='ps-4'>
              <SelectDropdown
                options={LANGUAGE_OPTIONS}
                initialSelectedOption={selectedLanguageOption}
                onOptionSelected={(option) => setSelectedLanguage(option.id as MeetingLanguage)}
                disabled={isAutoDetect}
              />
            </div>
          </div>
          <div className='d-flex flex-column gap-2 mt-2'>
            <div className='d-flex flex-column'>
              <Form.Check
                type='radio'
                id='auto-detect-option'
                name='language-option'
                label='Auto-detect'
                checked={isAutoDetect}
                onChange={() => setSelectedLanguage(MeetingLanguage.AUTO_DETECT)}
                className='paragraph-medium'
              />
            </div>
            <div className='ps-4'>
              <p className='paragraph-small mb-0'>
                AI Assistant will detect the spoken language during the meeting. If detection is wrong, <strong>your transcription will be inaccurate</strong>.
                Since Zocks produces <strong>no recordings</strong>, after the meeting, <strong>transcriptions cannot be recreated.</strong>
              </p>
            </div>
          </div>
          <div className='d-flex gap-3 justify-content-end py-3'>
            <Button variant='secondary' onClick={onHide}>
              Cancel
            </Button>
            <Button variant='primary' onClick={enableMultiLanguage} loading={isAccountFetching || isAccountLoading}>
              Enable
            </Button>
          </div>
        </Col>
      </Row>
    </Container>
  )
  return <Modal show header={modalHeader} body={body} backdrop='static' keyboard={false} />
}
